/**
 * Abstract class for building a cloud server prior to adding to basket.
 *
 * @abstract
 */
class AbstractCloudServerBuilder {
    /**
     * Object that gets passed to basket to create the basket item. Updates
     * automatically when choosing product options in the UI.
     *
     * @type {Object}
     */
    basketConfig = {
        spec: "",
        zone: "",
        optimisation: "Default",
        mvpsName: "",
        disk: "",
    }

    /**
     * Server configuration object used by the build server pages.
     *
     * This is the main entry point. Options are set here in the UI and then
     * reflected in other objects where the information is required.
     *
     * @type {Object}
     */
    pageConfig = {
        cloudProvider: "20i",
        configuredDiskSpace: 0,
        optimisationProfile: "Default",
        predictedBandwidth: 0,
        serverName: "",
        specName: "",
        timelineBackups: "standard",
        zone: "",
    }

    /**
     * Specification object that the cloud product helper uses to determine
     * pricing information. Updates automatically when choosing product options
     * in the UI.
     *
     * @type {Object}
     */
    specForCloudProductHelper = {
        bandwidth: "",
        country: "",
        cpu: "",
        disk: "",
        provider: "",
        ram: "",
        spec: "",
        zone: "",
    }

    /**
     * If importing an existing spec we don't build the object here as page
     * components won't re-render/update correctly. Use the import() method
     * instead.
     */
    constructor() {
        /** Prevent instantiating abstract class */
        if (this.constructor == AbstractCloudServerBuilder) {
            throw new Error(
                "AbstractCloudServerBuilder is an abstract class and can't be instantiated."
            )
        }
    }

    /** @type {string} Cloud provider e.g. 'aws' */
    get cloudProvider() {
        return this.pageConfig.cloudProvider
    }

    /** @param {string} cloud Cloud provider e.g. 'aws' */
    set cloudProvider(cloud) {
        this.pageConfig.cloudProvider = cloud
    }

    /** @type {(string | number)} Cloud product default CPU cores */
    get cpuCores() {
        return this.specForCloudProductHelper.cpu
    }

    /** @type {(string | number)} Disk space configuration option in GB */
    get configuredDiskSpace() {
        return this.pageConfig.configuredDiskSpace
    }

    /** @param {(string | number)} diskSpace Disk space configuration option in GB */
    set configuredDiskSpace(diskSpace) {
        let diskSpaceGb = +diskSpace
        this.pageConfig.configuredDiskSpace = +diskSpaceGb
        this.specForCloudProductHelper.disk = +diskSpaceGb
        this.basketConfig.disk = +diskSpaceGb
    }

    /**
     * Maximum amount of characters allowed for a server name
     * @type {number}
     */
    get maxServerNameLength() {
        return 50
    }

    /** @type {string} Server optimisation profile e.g. 'WordPress' */
    get optimisationProfile() {
        return this.pageConfig.optimisationProfile
    }

    /** @param {string} profile Server optimisation profile e.g. 'WordPress' */
    set optimisationProfile(profile) {
        this.pageConfig.optimisationProfile = profile
        this.basketConfig.optimisation = profile
    }

    /**
     * @type {(string | number)} Predicted monthly bandwidth usage in GB for
     * PAYG bandwidth providers
     */
    get predictedBandwidth() {
        return this.pageConfig.predictedBandwidth
    }

    /**
     * @param {(string | number)} bandwidth Predicted monthly bandwidth usage in GB for
     * PAYG bandwidth providers
     */
    set predictedBandwidth(bandwidth) {
        let bandwidthGb = +bandwidth
        this.pageConfig.predictedBandwidth = +bandwidthGb
    }

    /** @type {(string | number)} Cloud product default RAM in GB */
    get ramGb() {
        return this.specForCloudProductHelper.ram
    }

    /** @type {string} User specified server name */
    get serverName() {
        return this.pageConfig.serverName
    }

    /** @param {string} name User specified server name */
    set serverName(name) {
        if (
            name.match(/^[A-Za-z0-9\s\.\-]*$/) &&
            name.length <= this.maxServerNameLength
        ) {
            this.pageConfig.serverName = name
            this.basketConfig.mvpsName = name
        }
    }

    /** @type {string} The spec name for the chosen cloud server */
    get specName() {
        return this.pageConfig.specName
    }

    /** @param {string} name The spec name for the chosen cloud server */
    set specName(name) {
        this.pageConfig.specName = name
        this.basketConfig.spec = name
        this.specForCloudProductHelper.spec = name
    }

    /** @type {string} The spec name for the chosen cloud server */
    get timelineBackups() {
        return this.pageConfig.timelineBackups
    }

    /** @param {string} timelineBackupsOption Either 'standard' or 'pro' */
    set timelineBackups(timelineBackupsOption) {
        this.pageConfig.timelineBackups = timelineBackupsOption
    }

    /** @type {string} The zone/location code e.g. 'us-east-1' */
    get zone() {
        return this.pageConfig.zone
    }

    /** @param {string} zone The zone/location code e.g. 'us-east-1' */
    set zone(zone) {
        this.pageConfig.zone = zone
        this.basketConfig.zone = zone
        this.specForCloudProductHelper.zone = zone
    }

    /**
     * Export the page config for various purposes.
     *
     * @returns {Object}
     */
    exportConfig() {
        return this.pageConfig
    }

    /**
     * Import an existing config to build the object. Has to be done in a way
     * that updates build-server page components correctly.
     *
     * @param {Object} config An build-server pageConfig object.
     */
    importConfig(config) {
        this.optimisationProfile = config.optimisationProfile ?? this.optimisationProfile
        this.serverName = config.serverName ?? this.serverName
        this.cloudProvider = config.cloudProvider ?? this.cloudProvider
        this.timelineBackups = config.timelineBackups ?? this.timelineBackups

        // The UI needs to catch up before setting these
        setTimeout(() => {
            this.specName = config.specName ?? this.specName
        }, 800)

        setTimeout(() => {
            this.configuredDiskSpace = config.configuredDiskSpace ?? this.configuredDiskSpace
            this.predictedBandwidth = config.predictedBandwidth ?? this.predictedBandwidth
            this.zone = config.zone ?? this.zone
        }, 1000)
    }

    /**
     * Store the build server page config in a database over an API call.
     */
    saveToDatabase() {
        throw new Error("Not implemented.")
    }

    /**
     * Store the build server config in session storage to easily recall on page
     * reload.
     */
    saveToSessionStorage() {
        sessionStorage.setItem('cloudServerBuildSpec', JSON.stringify(this.exportConfig()))
    }

    /**
     * @param {string[]} zones
     * @returns {string} zone
     */
    selectBestZone(zones, currentZone) {
        const topZones = this.cloudProvider == "aws" ? [
                "us-east-1",
                "us-west-1"
            ] : [
                "dc_location-lhr",
                'us-west-1',
                'us-west2'
            ];

        if(zones.indexOf(currentZone) > -1){
            return currentZone
        }

        for (const zone of topZones) {
            if (zones.indexOf(zone) > -1) {
                return zone
            }
        }

        return zones[0]
    }

    /**
     * This is the main handler for product selection.
     *
     * @param {Object} cloudServer Cloud server product from the product JSON
     */
    selectCloudProduct(cloudServer, availableZones = null) {
        const zoneOptions = availableZones ?? cloudServer.detail.zone.options
        const diskRange = cloudServer.detail.disk.range

        /**
         * Required for all three objects (basketConfig, pageConfig and
         * specForCloudProductHelper) - respective setters used to update all
         * values.
         */
        this.specName = cloudServer.specName
        // console.log(this.zone);
        // console.log(this.optimisationProfile);

        /**
         * Only reset disk space to the product default when it hasn't been
         * configured yet or the current selection falls outside the newly
         * selected product's range. This preserves a user's chosen disk
         * space when the product is re-selected for unrelated reasons (e.g.
         * switching optimisation profile re-triggers the server slider).
         */
        if (
            !this.configuredDiskSpace ||
            this.configuredDiskSpace < diskRange.min ||
            this.configuredDiskSpace > diskRange.max
        ) {
            this.configuredDiskSpace = diskRange.min
        }
        this.zone = this.selectBestZone(zoneOptions, this.zone)

        /** pageConfig only */
        this.predictedBandwidth = cloudServer.detail.bandwidth.options[0]

        /**
         * Other specs for specForCloudProductHelper to assist with pricing
         * information that aren't covered in setters above.
         */
        this.specForCloudProductHelper.bandwidth = cloudServer.detail.bandwidth.options[0]
        this.specForCloudProductHelper.country = cloudServer.detail.country.options[0]
        this.specForCloudProductHelper.cpu = cloudServer.detail.cpu.options[0]
        this.specForCloudProductHelper.provider = cloudServer.detail.provider.options[0]
        this.specForCloudProductHelper.ram = cloudServer.detail.ram.options[0]
    }
}

/**
 * 20i Cloud Server builder class.
 *
 * @extends {AbstractCloudServerBuilder}
 */
class TwentyICloudServerBuilder extends AbstractCloudServerBuilder {
    /** @inheritdoc */
    constructor() {
        super()
    }
}

/**
 * HostShop Cloud Server builder class.
 *
 * Follows the Stack Billing 'SB' naming convention.
 *
 * @extends {AbstractCloudServerBuilder}
 */
class SBCloudServerBuilder extends AbstractCloudServerBuilder {
    /** @inheritdoc */
    constructor() {
        super()
    }
}
